import { Link } from 'react-router';
import type { OnTimeSplit, OverviewReport } from '#shared/types';
import { BarChart, LineChart } from '#client/components/charts';
import { Card } from '#client/components/ui/Card';
import { routes } from '#client/lib/routes';
import { SectionShell } from './SectionShell';
import { formatterFor } from './format';
import { heightForRows, isSeriesEmpty, onTimeCaption, turnaroundSubtitle } from './report.logic';
import { toSeries, toBarSeries, toOnTimeSeries } from './to-series';
import { useAnalyticsFilters } from './useAnalyticsFilters';
import { useAnalyticsSection } from './useAnalyticsSection';

/**
 * The landing screen: a router with numbers on it.
 *
 * Three charts, chosen for three different tempos — the daily rhythm, the shape
 * of the work, and whether it went out on time — and a link out of each into the
 * section that can actually explain it. No table: a table here would be a
 * drill-down that forgot to be a link.
 */
export function OverviewPage() {
  const filters = useAnalyticsFilters('overview');
  const { data, loading, error } = useAnalyticsSection<OverviewReport>('overview', filters);

  return (
    <SectionShell
      section="overview"
      filters={filters}
      data={data}
      loading={loading}
      error={error}
      metrics={data?.headline}
      empty={data !== undefined && isSeriesEmpty(data.registrations)}
    >
      {data && (
        <div className="c-an__grid">
          <WorkInOutCard report={data} />
          <BusiestTestsCard topTests={data.top_tests} />
          <TurnaroundCard split={data.turnaround} />
        </div>
      )}
    </SectionShell>
  );
}

function WorkInOutCard({
  report,
}: {
  report: Pick<OverviewReport, 'registrations' | 'completions'>;
}) {
  return (
    <Card
      className="c-an__cell c-an__cell--wide"
      title="Work in and work out"
      subtitle="The gap between the two is the backlog forming"
      actions={<Link to={routes.analyticsFlow}>See flow</Link>}
    >
      <LineChart
        title="Visits registered and reports released"
        series={[toSeries(report.registrations), toSeries(report.completions)]}
        format={formatterFor('count')}
        height={260}
        categoryLabel="Period"
      />
    </Card>
  );
}

function BusiestTestsCard({ topTests }: { topTests: OverviewReport['top_tests'] }) {
  return (
    <Card
      className="c-an__cell"
      title="Busiest tests"
      subtitle="By number booked"
      actions={<Link to={routes.analyticsVolume}>See volume</Link>}
    >
      <BarChart
        title="Tests by volume"
        orientation="horizontal"
        series={[toBarSeries('count', 'Booked', topTests)]}
        format={formatterFor('count')}
        height={heightForRows(topTests.length, 34, 180)}
        categoryLabel="Test"
      />
    </Card>
  );
}

function TurnaroundCard({ split }: { split: OnTimeSplit }) {
  return (
    <Card
      className="c-an__cell"
      title="Turnaround"
      subtitle={turnaroundSubtitle(split)}
      actions={<Link to={routes.analyticsFlow}>See flow</Link>}
    >
      <BarChart
        title="Reports against their committed turnaround"
        mode="stacked"
        series={toOnTimeSeries(split)}
        format={formatterFor('count')}
        height={180}
        categoryLabel="Outcome"
      />
      <p className="c-an__caption tnum">{onTimeCaption(split)}</p>
    </Card>
  );
}
